import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../services/api';

export default function AssessmentModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [assessmentInfo, setAssessmentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get first name from localStorage
  const getUserFirstName = () => {
    try {
      const userInfo = localStorage.getItem('userinfo');
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        return parsed.first_name || '';
      }
    } catch (err) {
      console.error('Error reading user info from localStorage:', err);
    }
    return '';
  };

  const userFirstName = getUserFirstName();

  useEffect(() => {
    if (isOpen) {
      fetchAssessmentInfo();
    }
  }, [isOpen]);

  const fetchAssessmentInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authApi.getAssessmentInfo();
      setAssessmentInfo(response.data);
    } catch (err) {
      console.error('Error fetching assessment info:', err);
      setError(err.response?.data?.detail || 'Failed to load assessment information');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssessment = () => {
    if (assessmentInfo?.oes_id) {
      navigate(`/oes/${assessmentInfo.oes_id}/assessment`);
      onClose();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-2xl font-bold text-eucnet-navy">Assessment Information</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-eucnet-blue"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchAssessmentInfo}
                className="px-4 py-2 bg-eucnet-blue text-white rounded-lg hover:bg-eucnet-navy"
              >
                Retry
              </button>
            </div>
          ) : !assessmentInfo?.has_assessment ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">
                {assessmentInfo?.message || 'No assessment assigned'}
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Welcome Section */}
              <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                <h3 className="text-xl font-bold mb-3 text-eucnet-navy">
                  Welcome{userFirstName ? `, ${userFirstName}` : ''}!
                </h3>
                <p className="text-gray-700 mb-4">
                  You have been assigned to participate in the <strong className="text-eucnet-navy">{assessmentInfo.assessment?.name}</strong> assessment. 
                  Your contribution is essential for evaluating the criticality and risk profile of your organization.
                </p>
                          <p className="text-gray-700 mb-4">
                            The assessment consists of 3 simple steps where you'll provide information about your organization's services, interdependencies, 
                            critical processes and automation, and potential impacts of cybersecurity incidents. You can save your work at any time and return to 
                            continue later.
                          </p>
                <div className="text-sm text-gray-600">
                  <p className="mb-1">
                    <strong>Need help?</strong> Contact your assessment administrator or refer to the help documentation.
                  </p>
                  <p>
                    Estimated completion time: 30-45 minutes
                  </p>
                </div>
              </div>
              {/* OES Info */}
              {assessmentInfo.oes && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  {assessmentInfo.oes.logo && (
                    <img
                      src={assessmentInfo.oes.logo.startsWith('data:') 
                        ? assessmentInfo.oes.logo 
                        : `data:image/jpeg;base64,${assessmentInfo.oes.logo}`}
                      alt={assessmentInfo.oes.name}
                      className="h-16 w-16 object-contain rounded"
                    />
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-eucnet-navy">{assessmentInfo.oes.name}</h3>
                    {assessmentInfo.oes.short_name && (
                      <p className="text-gray-600">{assessmentInfo.oes.short_name}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Assessment Info */}
              {assessmentInfo.assessment && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-eucnet-navy mb-2">
                      {assessmentInfo.assessment.name}
                    </h3>
                    {assessmentInfo.assessment.description && (
                      <p className="text-gray-600">{assessmentInfo.assessment.description}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">Start Date</div>
                      <div className="font-semibold text-gray-900">
                        {formatDate(assessmentInfo.assessment.start_date)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">End Date</div>
                      <div className="font-semibold text-gray-900">
                        {formatDate(assessmentInfo.assessment.end_date)}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-gray-500 mb-1">Status</div>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      assessmentInfo.assessment.status === 'active' 
                        ? 'bg-green-100 text-green-800'
                        : assessmentInfo.assessment.status === 'paused'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {assessmentInfo.assessment.status}
                    </span>
                  </div>

                  {/* Progress */}
                  {assessmentInfo.progress_percentage > 0 && (
                    <div>
                      <div className="text-sm text-gray-500 mb-2">Progress</div>
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div
                          className="bg-eucnet-blue h-4 rounded-full transition-all duration-300"
                          style={{ width: `${assessmentInfo.progress_percentage}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {assessmentInfo.progress_percentage}% Complete
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                {assessmentInfo.assessment?.status === 'active' && (
                  <button
                    onClick={handleStartAssessment}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-eucnet-primary to-eucnet-sky text-white rounded-lg hover:from-eucnet-navy hover:to-eucnet-primary transition-all font-semibold"
                  >
                    {assessmentInfo.progress_percentage > 0 ? 'Continue Assessment' : 'Start Assessment'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
